import { useState, useRef } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import { MdMyLocation } from "react-icons/md";

import {
  GoogleMap,
  MarkerF,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
};

const mapOptions = {
  streetViewControl: false,
  mapTypeControl: true,
  zoomControl: false,
  fullscreenControl: true,
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 }; // London

const AddressChangeModal = ({
  showModal,
  handleClose,
  changeAddress,
  userId,
}) => {
  const [address, setAddress] = useState("");
  const [marker, setMarker] = useState(defaultCenter);
  const [error, setError] = useState("");
  const [tag, setTag] = useState("");
  const [isBouncing, setIsBouncing] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  /* ---------------- MAP LOAD ---------------- */
  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  /* ---------------- GET CURRENT LOCATION ---------------- */
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newLocation = { lat, lng };

        setMarker(newLocation);

        if (mapRef.current) {
          mapRef.current.panTo(newLocation);
          mapRef.current.setZoom(17);
        }

        setIsBouncing(true);
        setTimeout(() => setIsBouncing(false), 1400);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newLocation }, (results, status) => {
          if (status === "OK" && results[0]) {
            setAddress(results[0].formatted_address);
            setError("");
          } else {
            setError("Could not find address for this location.");
          }
          setIsLocating(false);
        });
      },
      (error) => {
        console.error(error);
        setError("Unable to retrieve your location. Please allow access.");
        setIsLocating(false);
      },
    );
  };

  /* ---------------- AUTOCOMPLETE ---------------- */
  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();

    if (!place || !place.geometry) {
      setError("Please select an address from the list");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setAddress(place.formatted_address);
    setMarker({ lat, lng });

    // Animate marker briefly
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 1400);

    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(17);
  };

  /* ---------------- MAP CLICK ---------------- */
  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });

    // Animate marker briefly
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 1400);

    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(17);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      }
    });
  };

  /* ---------------- MARKER DRAG ---------------- */
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      }
    });
  };

  /* ---------------- SAVE ---------------- */
  const saveAddress = async () => {
    if (!address) {
      setError("Please select a valid address");
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SECRET}/api/DeliveryAddress`,
        {
          id: "",
          address,
          latitude: marker.lat,
          longitude: marker.lng,
          userId,
          tag,
          NoteForDeliveryMan: "N/A",
          isDefault: false,
          isLastDeliveryAddress: true,
          creationDate: new Date().toISOString(),
        },
      );

      changeAddress(res.data);
      handleClose();
    } catch {
      setError("Failed to save address");
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose} size="lg" enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>Select Delivery Address</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isLoaded && <p>Loading map...</p>}

        {isLoaded && (
          <>
            <Form.Label>Search UK Address:</Form.Label>
            <Autocomplete
              onLoad={(auto) => (autocompleteRef.current = auto)}
              onPlaceChanged={onPlaceChanged}
              options={{
                componentRestrictions: { country: "gb" },
                fields: ["geometry", "formatted_address"],
              }}
            >
              <Form.Control
                placeholder="Type UK address..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoComplete="off"
              />
            </Autocomplete>

            <div className="pt-2" style={{ position: "relative" }}>
              <GoogleMap
                options={mapOptions}
                mapContainerStyle={mapContainerStyle}
                center={marker}
                zoom={15}
                onLoad={onMapLoad}
                onClick={handleMapClick}
              >
                <MarkerF
                  position={marker}
                  draggable
                  animation={
                    isBouncing ? window.google.maps.Animation.BOUNCE : undefined
                  }
                  onDragEnd={handleMarkerDragEnd}
                />
              </GoogleMap>

              <button
                onClick={handleGetCurrentLocation}
                disabled={isLocating}
                style={{
                  position: "absolute",
                  bottom: "80px",
                  right: "10px",
                  zIndex: 1000,
                  backgroundColor: "#fff",
                  border: "2px solid #fff",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isLocating ? "not-allowed" : "pointer",
                  opacity: isLocating ? 0.6 : 1,
                  padding: 0,
                  outline: "none",
                }}
                title="My Location"
              >
                <MdMyLocation
                  size={24}
                  color={isLocating ? "#999" : "#4285f4"}
                />
              </button>
            </div>

            <div className="pt-2">
              <Form.Label>Tag:</Form.Label>
              <Form.Control
                className="mt-1"
                placeholder="Tag (Home, Office etc)"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              />
            </div>
          </>
        )}

        {error && <div className="text-danger mt-2">{error}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={saveAddress} disabled={!isLoaded}>
          Save Address
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddressChangeModal;
